import React from "react"
import { motion, AnimatePresence } from "framer-motion"

interface IntroductionStatusProps {
  status?: "waiting" | "ready" | "multiple"
  count?: number
  onView?: () => void
}

export function IntroductionStatus({
  status = "waiting",
  count = 1,
  onView,
}: IntroductionStatusProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-6 py-12">
      <AnimatePresence mode="wait">
        {status === "waiting" ? (
          <motion.div
            key="waiting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative mb-12"
            >
              <motion.div
                className="logo-gradient-orb"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-8"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-niyyah-pale/50 border border-niyyah-light/20">
                <motion.span
                  className="w-1.5 h-1.5 rounded-full bg-niyyah-purple"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                />
                <span className="text-xs text-niyyah-muted font-light tracking-wide">
                  Preparing your first introduction
                </span>
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-center max-w-md"
            >
              <p className="text-lg md:text-xl text-niyyah-text font-light leading-relaxed mb-4">
                We&apos;re identifying matches based on your values and preferences.
              </p>
              <p className="text-base text-niyyah-muted font-light leading-relaxed">
                You&apos;ll be notified when a match is available.
              </p>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="ready"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center text-center"
          >
            <div className="relative mb-10">
              <motion.div
                className="absolute inset-0 rounded-full bg-niyyah-purple/20"
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="match-count-circle">
                <span className="text-3xl font-light text-white relative z-10">
                  {status === "multiple" ? count : 1}
                </span>
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl font-light text-niyyah-text mb-4">
              {status === "multiple" ? "Introductions Ready" : "Introduction Ready"}
            </h2>

            <p className="text-lg text-niyyah-muted font-light leading-relaxed mb-10 max-w-md">
              {status === "multiple"
                ? `We&apos;ve identified ${count} potential paths aligned with your values.`
                : "We&apos;ve identified a potential path aligned with your values."}
              <br />
              <span className="text-sm opacity-80 mt-2 block">Take a moment to review privately.</span>
            </p>

            <button onClick={onView} className="view-intro-button" type="button">
              <span className="text-sm font-light leading-tight relative z-10">
                View
                <br />
                Introduction
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .logo-gradient-orb {
          position: relative;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background-image:
            url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"),
            linear-gradient(
              135deg,
              #7a6ba8 0%,
              #8b7db8 12%,
              #9b8fc4 25%,
              #8c9ed8 38%,
              #7ba5d9 50%,
              #6bc4e0 62%,
              #b89bc8 75%,
              #9b8fc4 88%,
              #7a6ba8 100%
            );
          background-size: 200px 200px, 400% 400%;
          background-blend-mode: overlay, normal;
          box-shadow: 0 0 40px rgba(155, 135, 199, 0.6);
          animation: logoGradientFluctuate 12s ease-in-out infinite;
        }

        @keyframes logoGradientFluctuate {
          0%, 100% { background-position: 0px 0px, 0% 50%; }
          25% { background-position: 0px 0px, 100% 50%; }
          50% { background-position: 0px 0px, 50% 100%; }
          75% { background-position: 0px 0px, 0% 100%; }
        }

        .match-count-circle {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #9b8fc4;
          overflow: hidden;
        }

        .match-count-circle::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.5;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        .view-intro-button {
          position: relative;
          width: 110px;
          height: 110px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: transform 0.3s ease;
          overflow: hidden;
          background: #9b8fc4;
          border: none;
          cursor: pointer;
        }

        .view-intro-button::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.5;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        .view-intro-button:hover { transform: scale(1.05); }
      `}</style>
    </div>
  )
}
